import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { ShieldCheck, Car, Home as HomeIcon } from "lucide-react";
import { MaskLines } from "../site/Reveal";
import { CtaCluster } from "../site/Cta";
import { OpenBadge } from "../site/OpenBadge";

const EASE = [0.16, 1, 0.3, 1];

function TiltFrame() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div ref={ref} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[118%] w-[118%] -translate-x-1/2 -translate-y-1/2">
        <div className="spin-slow absolute inset-0 rounded-full border border-dashed border-white/20" />
        <div className="spin-slow-rev absolute inset-[14%] rounded-full border border-dashed border-white/15" />
      </div>
      <motion.div style={{ rotateX: rx, rotateY: ry }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.35, ease: EASE }} className="relative [transform-style:preserve-3d]">
        <div className="windshield-frame relative aspect-[5/4] overflow-hidden bg-black shadow-[0_40px_120px_-30px_rgba(64,95,242,0.55)]">
          <motion.img style={{ y: imgY }} src="/images/facade-haguenau.webp" alt="Façade du centre A+GLASS Pare-Brise HAGUENAU, 101A Route de Marienthal" className="absolute inset-0 h-[118%] w-full -top-[9%] object-cover object-[60%_50%]" fetchpriority="high" width="1600" height="1213" />
          <div className="absolute inset-0 bg-gradient-to-t from-ag-ink/70 via-transparent to-transparent" />
          <div className="glass-sheen absolute inset-0" />
        </div>
        <div className="absolute -bottom-5 left-4 right-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-white/10 p-3 ring-1 ring-white/20 backdrop-blur-xl [transform:translateZ(40px)] sm:left-8 sm:right-auto">
          <OpenBadge dark testid="hero-open-badge" />
        </div>
      </motion.div>
    </div>
  );
}

export function Hero() {
  return (
    <section className="hero-grain relative overflow-hidden bg-ag-ink px-5 pb-24 pt-32 text-white lg:px-8 lg:pb-32 lg:pt-40" data-testid="home-hero">
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-ag-blue/25 blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1320px] items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-ag-yellow ring-1 ring-white/15">
            A+GLASS Pare-Brise HAGUENAU
          </motion.p>
          <h1 className="mt-6 font-display text-[2.7rem] font-extrabold leading-[0.95] tracking-[-0.03em] sm:text-6xl lg:text-[5.6rem]" data-testid="home-h1">
            <MaskLines lines={["Pare-Brise", "à Haguenau –"]} />
            <MaskLines lines={["A+ Glass"]} lineClass="text-ag-yellow" delay={0.34} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55, ease: EASE }} className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
            Spécialiste du pare-brise, vitrage automobile et éclairage pour tous véhicules à Haguenau.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7, ease: EASE }} className="mt-9">
            <CtaCluster dark prefix="hero-cta" />
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }} className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-300">
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-ag-yellow" />Agréé par les assurances</li>
            <li className="flex items-center gap-2"><Car className="h-4 w-4 text-ag-yellow" />Prêt de véhicule selon disponibilité</li>
            <li className="flex items-center gap-2"><HomeIcon className="h-4 w-4 text-ag-yellow" />Intervention à domicile selon secteur</li>
          </motion.ul>
        </div>
        <div className="lg:col-span-5"><TiltFrame /></div>
      </div>
    </section>
  );
}
